import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { RasterPicker, SearchOnlyPicker } from "./LeafletPicker";
import { MapChrome } from "./MapChrome";
import { hasWebGL2, isBrave, ESRI_TILES } from "../lib/maps";
import { vectorDisabled, rememberFallback, publishDiag } from "../lib/mapHealth";
export { hasWebGL2 };

const MapPicker = lazy(() => import("./MapPicker").then((m) => ({ default: m.MapPicker })));

class MapBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { this.props.onFail?.({ reason: "exception", message: err?.message }); }
  render() { return this.state.failed ? null : this.props.children; }
}

const initialProvider = () => (hasWebGL2() && !vectorDisabled() ? "vector" : ESRI_TILES ? "esri" : "none");

/* Provider orchestration: vector (OpenFreeMap) → Esri raster → search-only placeholder, with loading shimmer, hint and Reload. */
export const SitePicker = ({ pin, onPin }) => {
  const [provider, setProvider] = useState(initialProvider);
  const [gen, setGen] = useState(0);
  const [loading, setLoading] = useState(true);
  const [switched, setSwitched] = useState(() => vectorDisabled());
  const [lost, setLost] = useState(false);

  const fail = useCallback((diag) => {
    if (diag?.provider !== "esri" && provider === "vector") { rememberFallback(diag); setSwitched(true); setProvider(ESRI_TILES ? "esri" : "none"); }
    else setProvider("none");
    setLoading(false); setGen((g) => g + 1);
  }, [provider]);
  const ready = useCallback(() => setLoading(false), []);
  const reinit = useCallback(() => { setProvider(initialProvider()); setSwitched(vectorDisabled()); setLoading(true); setLost(false); setGen((g) => g + 1); }, []);
  // WebGL context lost (Android backgrounding): re-create the map when the tab is visible again, keeping the pin
  const losses = useRef([]);
  const contextLost = useCallback(() => {
    const now = Date.now(); losses.current = [...losses.current.filter((t) => now - t < 60000), now];
    if (losses.current.length > 2) return fail(publishDiag({ provider: "openfreemap", reason: "webglcontextlost-repeated", failedResources: losses.current.length }));
    setLost(true);
  }, [fail]);
  useEffect(() => {
    if (!lost) return;
    if (!document.hidden) { reinit(); return; }
    const onVis = () => { if (!document.hidden) reinit(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [lost, reinit]);

  return (
    <div className="map-wrap" data-testid={provider === "vector" ? "map-picker" : "map-picker-fallback"} data-provider={provider === "vector" ? "openfreemap" : provider} data-loading={loading ? "true" : "false"}>
      {provider === "vector" && (
        <MapBoundary key={`b${gen}`} onFail={fail}>
          <Suspense fallback={<div className="map-canvas" data-testid="map-loading" />}>
            <MapPicker key={`v${gen}`} pin={pin} onPin={onPin} onFail={fail} onReady={ready} onContextLost={contextLost} />
          </Suspense>
        </MapBoundary>
      )}
      {provider === "esri" && <RasterPicker key={`r${gen}`} pin={pin} onPin={onPin} onFail={fail} onReady={ready} />}
      {provider === "none" && <SearchOnlyPicker pin={pin} onPin={onPin} />}
      <MapChrome loading={loading && provider !== "none"} switched={switched} onReload={reinit} />
      {switched && isBrave() && <p className="map-brave-hint" data-testid="brave-hint">Brave's Shields can block map rendering — tap the lion icon and set Shields to Standard or allow fingerprinting for dropit.fyi.</p>}
    </div>
  );
};
